﻿import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/api/client'
import { useAuthStore } from './auth'
import { useNotificationStore } from './notification'

export const useStocksStore = defineStore('stocks', () => {
  // State
  const watchlist = ref([])
  const stockPool = ref([])
  const searchResults = ref([])
  const loading = ref(false)
  const error = ref(null)
  const ws = ref(null)
  const wsConnected = ref(false)
  const realtimeData = ref({})  // {stock_code: realtime_data}
  const signals = ref({})  // {stock_code: [signal1, signal2, ...]} - 信号数据
  const realtimeKlineData = ref({}) // {stock_code: kline_data[]} - 实时K线数据
  const holdingOptimizerSummary = ref(null)
  const holdingOptimizerLoading = ref(false)

  // Actions
  async function fetchWatchlist(forceUpdate = false) {
    loading.value = true
    error.value = null

    try {
      const response = await api.get('/api/stocks/watchlist', {
        params: {
          include_realtime: true,
          force_update: forceUpdate
        }
      })
      // 后端返回格式: { code: 0, msg: "success", data: [...] }
      watchlist.value = response.data.data || []
    } catch (err) {
      error.value = err.response?.data?.detail || '获取自选股失败'
      watchlist.value = []
    } finally {
      loading.value = false
    }
  }

  async function fetchHoldingOptimizerSummary() {
    holdingOptimizerLoading.value = true

    try {
      const response = await api.get('/api/stocks/watchlist/holding-optimizer/latest')
      holdingOptimizerSummary.value = response.data.data || null
      return holdingOptimizerSummary.value
    } catch (err) {
      holdingOptimizerSummary.value = {
        status: 'error',
        message: err.response?.data?.detail || '获取最佳持有天数失败',
        groups: []
      }
      return holdingOptimizerSummary.value
    } finally {
      holdingOptimizerLoading.value = false
    }
  }

  async function addToWatchlist(stockCode, strategyGroupId = null, strategyFileId = null, sourceType = null) {
    loading.value = true
    error.value = null

    try {
      const response = await api.post('/api/stocks/watchlist', {
        stock_code: stockCode,
        strategy_group_id: strategyGroupId,
        strategy_file_id: strategyFileId,
        source_type: sourceType
      })

      // 检查响应状态码
      if (response.data.code === 0) {
        // 成功添加（包括自动匹配成功）
        const data = response.data.data

        // 添加到本地列表
        if (data) {
          watchlist.value.push(data)
        }

        // 更新异动标识
        await fetchVolatilityAlerts()

        return {
          success: true,
          autoMatched: data?.auto_matched || false,
          matchInfo: data?.match_info,
          message: response.data.msg
        }
      } else if (response.data.code === 1) {
        // 需要用户选择策略
        return {
          success: false,
          needStrategySelection: true,
          stockCode: response.data.data.stock_code,
          fullMatches: response.data.data.full_matches || [],
          partialMatches: response.data.data.partial_matches || [],
          message: response.data.msg
        }
      } else {
        throw new Error(response.data.msg || '添加失败')
      }
    } catch (err) {
      error.value = err.response?.data?.detail || '添加自选股失败'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function addBatchToWatchlist(stockCodes, strategyGroupId, strategyFileId, sourceType = null) {
    loading.value = true
    error.value = null

    try {
      const response = await api.post('/api/stocks/watchlist/batch-add', {
        stock_codes: stockCodes,
        strategy_group_id: strategyGroupId,
        strategy_file_id: strategyFileId,
        source_type: sourceType
      })

      const data = response.data.data || {}
      const successItems = data.success_items || []
      const skippedCodes = data.skipped_codes || []
      const failedItems = data.failed_items || []

      if (successItems.length > 0) {
        const existingCodes = new Set(
          watchlist.value.map(stock => stock.code || stock.stock_code).filter(Boolean)
        )
        const mergedItems = successItems.filter(item => {
          const stockCode = item.code || item.stock_code
          if (!stockCode || existingCodes.has(stockCode)) return false
          existingCodes.add(stockCode)
          return true
        })
        watchlist.value.push(...mergedItems)
      }

      if (successItems.length > 0) {
        await fetchVolatilityAlerts()
      }

      return {
        success: true,
        message: response.data.msg,
        successItems,
        successCodes: data.success_codes || successItems.map(item => item.code || item.stock_code),
        skippedCodes,
        failedItems,
        totalRequested: data.total_requested || stockCodes.length,
        totalDeduplicated: data.total_deduplicated || stockCodes.length
      }
    } catch (err) {
      error.value = err.response?.data?.detail || '批量添加自选股失败'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function confirmAddToWatchlist(stockCode, strategyGroupId, strategyFileId) {
    loading.value = true
    error.value = null

    try {
      const response = await api.post('/api/stocks/watchlist/confirm', {
        stock_code: stockCode,
        strategy_group_id: strategyGroupId,
        strategy_file_id: strategyFileId
      })

      // 后端返回格式: { code: 0, msg: "success", data: {...} }
      const data = response.data.data

      // 添加到本地列表
      if (data) {
        watchlist.value.push(data)
      }

      // 更新异动标识
      await fetchVolatilityAlerts()

      return { success: true, message: response.data.msg }
    } catch (err) {
      error.value = err.response?.data?.detail || '确认添加自选股失败'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function removeFromWatchlist(stockCode, clearPosition = false) {
    loading.value = true
    error.value = null

    try {
      await api.delete(`/api/stocks/watchlist/${stockCode}`, {
        params: { clear_position: clearPosition }
      })

      // 从本地列表移除
      watchlist.value = watchlist.value.filter(
        stock => stock.code !== stockCode
      )

      return { success: true }
    } catch (err) {
      error.value = err.response?.data?.detail || '删除自选股失败'
      throw err
    } finally {
      loading.value = false
    }
  }

  async function searchStocks(keyword, options = {}) {
    if (!keyword || keyword.trim().length === 0) {
      searchResults.value = []
      return []
    }

    try {
      const params = { q: keyword }

      // 支持分页参数
      if (options.limit) params.limit = options.limit
      if (options.offset) params.offset = options.offset

      const response = await api.get('/api/stocks/search', { params })
      // 后端返回格式: { code: 0, msg: "success", data: [...] }
      const data = response.data.data || []

      // 如果不是分页请求，更新searchResults
      if (!options.offset) {
        searchResults.value = data
      }

      return data
    } catch (err) {
      if (!options.offset) {
        searchResults.value = []
      }
      return []
    }
  }

  async function getStockDetail(stockCode) {
    try {
      const response = await api.get(`/api/stocks/${stockCode}/detail`)
      // 后端返回格式: { code: 0, msg: "success", data: {...} }
      return response.data.data
    } catch (err) {
      throw err
    }
  }

  async function getRealtimeData(stockCode) {
    try {
      const response = await api.get(`/data/realtime/${stockCode}`)
      return response.data
    } catch (err) {
      throw err
    }
  }

  async function getRealtimeBatch(stockCodes) {
    try {
      const response = await api.get('/data/realtime/batch', {
        params: { codes: stockCodes.join(',') }
      })
      return response.data
    } catch (err) {
      throw err
    }
  }

  async function checkShouldRefresh() {
    try {
      const response = await api.get('/api/stocks/watchlist/should-refresh')
      // 后端返回格式: { code: 0, msg: "success", data: { should_refresh, reason, last_update } }
      return response.data.data || { should_refresh: true, reason: '允许刷新' }
    } catch (err) {
      // 如果检查失败，默认允许刷新
      return { should_refresh: true, reason: '无法判断，允许刷新' }
    }
  }

  function clearSearchResults() {
    searchResults.value = []
  }

  // WebSocket连接
  function connectWebSocket() {
    const authStore = useAuthStore()

    if (!authStore.token || ws.value) {
      return
    }

    try {
      // 动态选择WebSocket URL
      // 注意：WebSocket代理在nginx中配置为 /api/ws/，不需要子路径前缀
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const host = window.location.host

      // WebSocket直接使用 /api/ws/stocks 路径，nginx会代理到scheduler服务
      const wsUrl = `${protocol}//${host}/api/ws/stocks?token=${authStore.token}`
      ws.value = new WebSocket(wsUrl)

      ws.value.onopen = () => {
        wsConnected.value = true

        // 发送心跳包
        const heartbeat = setInterval(() => {
          if (ws.value?.readyState === WebSocket.OPEN) {
            ws.value.send('ping')
          } else {
            clearInterval(heartbeat)
          }
        }, 30000) // 30秒心跳
      }

      ws.value.onmessage = (event) => {
        try {
          if (event.data === 'pong') {
            return // 心跳响应
          }

          const message = JSON.parse(event.data)
          const timestamp = new Date().toLocaleTimeString()
          
          console.log(`📨 [WebSocket ${timestamp}] 收到消息类型: ${message.type}`)
          
          if (message.type === 'stock_update') {
            console.log(`📈 [stock_update] 包含 ${message.data?.length || 0} 只股票`)
            updateRealtimeData(message.data)
          } else if (message.type === 'candidate_update') {
            console.log(`📋 [candidate_update] 包含 ${message.data?.length || 0} 只备选股`)
            updateCandidateRealtimeData(message.data)
          } else if (message.type === 'market_index_update') {
            console.log(`📊 [market_index_update] 包含 ${message.data?.length || 0} 个指数`)
            updateMarketIndices(message.data)
          } else if (message.type === 'signal_update') {
            console.log(`🔔 [signal_update] 股票: ${message.data?.stock_code}`)
            handleSignalUpdate(message.data)
          } else if (message.type === 'volatility_alert_update') {
            console.log(`⚠️ [volatility_alert_update] 类型: ${message.update_type}`)
            handleVolatilityAlertUpdate(message)
          } else if (message.type === 'hot_news_alert') {
            console.log(`📰 [hot_news_alert] 新闻: ${message.data?.title}`)
            handleHotNewsAlert(message.data)
          } else if (message.type === 'kline_1m_update') {
            console.log(`📉 [kline_1m_update] 股票: ${message.stock_code}, K线数据点: ${message.data?.length || 0}`)
            if (message.stock_code && message.data) {
              realtimeKlineData.value[message.stock_code] = message.data
              console.log(`✅ [kline_1m_update] ${message.stock_code} K线数据已更新到 realtimeKlineData，不影响价格显示`)
            }
          } else {
            console.warn(`❓ [未知消息类型] ${message.type}`)
          }
        } catch (err) {
          console.error('❌ 处理WebSocket消息失败:', err, event.data)
        }
      }

      ws.value.onerror = (error) => {
        wsConnected.value = false
      }

      ws.value.onclose = (event) => {
        wsConnected.value = false
        ws.value = null

        // 5秒后重连（如果不是主动关闭）
        if (event.code !== 1000 && authStore.token) {
          setTimeout(() => {
            connectWebSocket()
          }, 5000)
        }
      }

    } catch (err) {
      wsConnected.value = false
    }
  }

  // 断开WebSocket
  function disconnectWebSocket() {
    if (ws.value) {
      ws.value.close(1000, 'User disconnect')
      ws.value = null
      wsConnected.value = false
    }
  }

  // 更新实时数据
  function updateRealtimeData(newStocks) {
    if (!Array.isArray(newStocks)) {
      console.warn('⚠️ updateRealtimeData: 收到非数组数据', newStocks)
      return
    }
    
    console.log(`📊 [updateRealtimeData] 收到 ${newStocks.length} 只股票的实时数据更新`)
    
    // 更新realtimeData缓存
    newStocks.forEach(stock => {
      const oldPrice = realtimeData.value[stock.code]?.price
      realtimeData.value[stock.code] = stock
      
      // 如果价格变化，打印日志
      if (oldPrice && oldPrice !== stock.price) {
        console.log(`💰 [价格更新] ${stock.code}: ${oldPrice} → ${stock.price} (${stock.change_percent > 0 ? '+' : ''}${(stock.change_percent * 100).toFixed(2)}%)`)
      }
    })

    // 直接更新watchlist中的数据（触发响应式更新）
    watchlist.value.forEach((item, index) => {
      const realtime = realtimeData.value[item.code]
      if (realtime) {
        const oldPrice = item.price
        
        // 直接修改对象属性以触发Vue的响应式更新
        watchlist.value[index] = {
          ...item,
          price: realtime.price,
          change_percent: realtime.change_percent,
          volume: realtime.volume,
          high: realtime.high,
          low: realtime.low,
          open: realtime.open,
          prev_close: realtime.prev_close,
          last_update: realtime.timestamp
        }
        
        // 如果价格变化超过0.5%，打印警告
        if (oldPrice && Math.abs(realtime.price - oldPrice) / oldPrice > 0.005) {
          console.warn(`⚠️ [价格突变] ${item.code} ${item.name}: ${oldPrice} → ${realtime.price} (变化${((realtime.price - oldPrice) / oldPrice * 100).toFixed(2)}%)`)
        }
      }
    })
  }

  // 更新备选股实时数据
  function updateCandidateRealtimeData(candidates) {
    if (!Array.isArray(candidates)) {
      return
    }
    // 更新realtimeData缓存（使用stock_code作为key）
    candidates.forEach(candidate => {
      realtimeData.value[candidate.stock_code] = {
        code: candidate.stock_code,
        name: candidate.stock_name,
        price: candidate.price,
        change_percent: candidate.change_percent,
        change_amount: candidate.change_amount,
        volume: candidate.volume,
        amount: candidate.amount,
        high: candidate.high,
        low: candidate.low,
        open: candidate.open,
        prev_close: candidate.prev_close,
        timestamp: candidate.timestamp
      }
    })
  }

  // 更新大盘指数数据
  const marketIndices = ref([])

  function updateMarketIndices(newIndices) {
    if (!Array.isArray(newIndices)) {
      return
    }

    marketIndices.value = newIndices
  }

  // 获取大盘指数数据（初始加载）
  async function fetchMarketIndices() {
    try {
      const response = await api.get('/api/market/indices')
      if (response.data.code === 0) {
        marketIndices.value = response.data.data || []
        // 标记无效数据但不过滤掉，让用户看到所有配置的指数
        marketIndices.value = marketIndices.value.map(index => {
          const hasValidPrice = index.price && index.price > 0
          if (!hasValidPrice) {
            console.warn(`⚠️ 指数数据异常: ${index.index_name || index.name} (${index.index_code || index.code}), 价格: ${index.price}`)
            // 添加异常标记
            return {
              ...index,
              _hasDataIssue: true,
              _issueReason: '数据源暂无数据'
            }
          }
          return index
        })
      }
    } catch (err) {
      marketIndices.value = []
    }
  }

  // 获取股票颜色类
  function getStockColorClass(changePercent) {
    if (changePercent > 0) return 'text-red-500'  // 上涨红色
    if (changePercent < 0) return 'text-green-500'  // 下跌绿色
    return 'text-gray-500'  // 平盘灰色
  }

  // 格式化涨跌幅（后端传的是小数形式，如0.0517表示5.17%）
  function formatChangePercent(changePercent) {
    if (changePercent === 0) return '0.00%'
    const sign = changePercent > 0 ? '+' : ''
    return `${sign}${(changePercent * 100).toFixed(2)}%`
  }

  // 处理信号更新
  function handleSignalUpdate(data) {
    if (!data || !data.stock_code) {
      return
    }

    const stockCode = data.stock_code

    // 处理通用信号更新
    if (data.signals) {
      const newSignals = data.signals || []
      // 更新signals状态
      signals.value[stockCode] = newSignals

      // 更新自选股中的信号数据
      watchlist.value.forEach((item, index) => {
        if (item.code === stockCode) {
          watchlist.value[index] = {
            ...item,
            signals: newSignals
          }
        }
      })
    }

    // 处理买入/卖出/移除信号状态更新
    if (data.signal_type) {
      watchlist.value.forEach((item, index) => {
        if (item.code === stockCode) {
          const updates = { ...item }

          if (data.signal_type === 'buy_signal') {
            updates.buy_signal_sent = data.triggered || false
            updates.buy_signal_date = data.signal_date || null
          } else if (data.signal_type === 'sell_signal') {
            updates.sell_signal_sent = data.triggered || false
            updates.sell_signal_date = data.signal_date || null
          } else if (data.signal_type === 'removal_signal') {
            updates.removal_signal_sent = data.triggered || false
            updates.removal_signal_date = data.signal_date || null
          }

          watchlist.value[index] = updates
        }
      })
    }
  }

  // 获取股票的活跃信号
  async function getStockSignals(stockCode) {
    try {
      const response = await api.get(`/api/signals/${stockCode}`)
      if (response.data.code === 0) {
        const stockSignals = response.data.data || []
        signals.value[stockCode] = stockSignals
        return stockSignals
      }
      return []
    } catch (err) {
      return []
    }
  }

  // 批量获取信号
  async function getSignalsBatch(stockCodes) {
    try {
      const response = await api.get('/api/signals/batch', {
        params: { stock_codes: stockCodes.join(',') }
      })
      if (response.data.code === 0) {
        const batchSignals = response.data.data || {}
        // 更新signals状态
        Object.keys(batchSignals).forEach(code => {
          signals.value[code] = batchSignals[code]
        })
        return batchSignals
      }
      return {}
    } catch (err) {
      return {}
    }
  }

  // 严重异动预警相关状态
  const volatilityAlerts = ref({}) // {stock_code: alert_data}

  // 处理严重异动预警更新
  function handleVolatilityAlertUpdate(message) {
    const { update_type, data } = message

    if (update_type === 'new_alerts') {
      // 新增预警
      if (Array.isArray(data)) {
        data.forEach(alert => {
          volatilityAlerts.value[alert.stock_code] = alert
        })
      }
    } else if (update_type === 'resolved_alerts') {
      // 解除预警
      if (Array.isArray(data)) {
        data.forEach(stockCode => {
          delete volatilityAlerts.value[stockCode]
        })
      }
    } else if (update_type === 'triggered_volatility') {
      // 处理触发异动更新
      if (data && data.stock_code) {

        // 更新watchlist中对应股票的last_volatility_trigger_at字段
        const stock = watchlist.value.find(s => s.code === data.stock_code)
        if (stock) {
          stock.last_volatility_trigger_at = data.trigger_time
        }
      }
    } else if (update_type === 'volatility_cleared') {
      // 处理异动解除更新
      if (data && data.stock_code) {
        // 清除watchlist中对应股票的last_volatility_trigger_at字段
        const stock = watchlist.value.find(s => s.code === data.stock_code)
        if (stock) {
          stock.last_volatility_trigger_at = null
        }
      }
    }
  }

  // 获取严重异动预警
  async function fetchVolatilityAlerts() {
    try {
      const response = await api.get('/api/severe-volatility/alerts/watchlist')
      if (response.data.code === 0) {
        const alertsMap = {}
        response.data.data.alerts.forEach(alert => {
          alertsMap[alert.stock_code] = alert
        })
        volatilityAlerts.value = alertsMap
        return alertsMap
      }
      return {}
    } catch (err) {
      console.error('获取严重异动预警失败:', err)
      return {}
    }
  }

  // Handle Hot News Alert
  function handleHotNewsAlert(data) {
    // 只有当影响力达到列表可见门槛 (>= 7) 时才增加未读红点计数，避免不可见新闻污染红点
    if (data && data.impact_level >= 7) {
      const notificationStore = useNotificationStore()
      notificationStore.incrementHotNews(1)
    }

    // Optionally show a toast notification
    // import { notify } from '@/services/notificationService'
    // notify.info(`New Hot News: ${data.title}`)
  }

  return {
    watchlist,
    stockPool,
    searchResults,
    loading,
    error,
    ws,
    wsConnected,
    realtimeData,
    holdingOptimizerSummary,
    holdingOptimizerLoading,
    marketIndices,
    signals,
    volatilityAlerts,
    fetchWatchlist,
    fetchHoldingOptimizerSummary,
    addToWatchlist,
    addBatchToWatchlist,
    confirmAddToWatchlist,
    removeFromWatchlist,
    searchStocks,
    getStockDetail,
    getRealtimeData,
    getRealtimeBatch,
    checkShouldRefresh,
    clearSearchResults,
    connectWebSocket,
    disconnectWebSocket,
    updateRealtimeData,
    updateMarketIndices,
    fetchMarketIndices,
    getStockColorClass,
    formatChangePercent,
    handleSignalUpdate,
    getStockSignals,
    getSignalsBatch,
    handleVolatilityAlertUpdate,
    fetchVolatilityAlerts,
    handleVolatilityAlertUpdate,
    fetchVolatilityAlerts,
    handleHotNewsAlert,
    realtimeKlineData,

    // K-line Subscription
    subscribeToKline: (stockCode) => {
      if (ws.value && ws.value.readyState === WebSocket.OPEN) {
        ws.value.send(JSON.stringify({
          type: 'subscribe_kline',
          stock_code: stockCode
        }))
      }
    },
    unsubscribeFromKline: (stockCode) => {
      if (ws.value && ws.value.readyState === WebSocket.OPEN) {
        ws.value.send(JSON.stringify({
          type: 'unsubscribe_kline',
          stock_code: stockCode
        }))
      }
    }
  }
})

